import { loginRequest, msalInstance } from 'app/config';

import { getJWT } from './apiHelpers';
import { APIError } from './apiTypes';
import { serializeRequestBody } from './serializeRequestBody';

// Re-export types for backward compatibility
export type { APIBasePaginatedResponse, APIBaseResponse, APIMethod } from './apiTypes';
export { APIError } from './apiTypes';

type APIMethodsUnionType = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export async function _request<TResponse>(
    method: APIMethodsUnionType,
    url: string,
    body?: object | string | FormData,
    signal?: AbortSignal,
    retries = 1,
    delay = 1000,
): Promise<TResponse> {
    const baseURL = import.meta.env.VITE_API_BASE_URL;

    const jwtToken = await getAccessToken();
    if (!jwtToken) {
        throw new APIError(401);
    }

    for (let i = 0; i < retries; i++) {
        try {
            const headers: {[key: string]: string} = {
                'Authorization': `Bearer ${jwtToken.accessToken}`
            };
            if (!(body instanceof FormData)) {
                headers['Content-Type'] = 'application/json';
            }
            const response = await globalThis.fetch(`${baseURL}${url}`, {
                method,
                body: body ? getRequestBody(body) : undefined,
                headers: headers,
                signal: signal
            });

            const result = await getRequestResult(response);
            return result as TResponse;
        }
        catch (error) {
            if (i < retries - 1) {
                await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
            } else {
                throw error;
            }
        }
    }
    throw new Error('Never reached');
}

const getAccessToken = async() => {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    const request = { ...loginRequest, account };
    return await getJWT(request);
};

function getRequestBody(body: object | string | FormData): string | FormData | undefined {
    if (body instanceof FormData) {
        return body;
    }

    return serializeRequestBody(body);
}

const getRequestResult = async (response: Response) => {
    const result = await response.json();

    if (response.status < 200 || response.status >= 300) { // Network error
        throw new APIError(response.status, result.error);
    }

    if (!result.success) { // Application error
        throw new APIError(response.status);
    }

    return result;
};
